/**
 * ServiceCardsSection
 *
 * A full-width section with a heading + description on the left/top, and a
 * 3-column grid of dark cards below. Each card shows a title, a white
 * horizontal line accent, a description, and an icon image at the bottom.
 *
 * Used on consulting service pages to highlight key service offerings.
 * Also used to replace the "Our expertise" section in GoogleAdWordsService.
 *
 * Props:
 * - bgColor (string, required): Tailwind background class for the section (e.g. "bg-orange", "bg-blue").
 * - title (string, required): The section heading text. An accent-colored period is appended automatically.
 * - titleColor (string, optional): Tailwind text color class for the heading. Defaults to "text-blue-navy".
 * - accentColor (string, optional): Tailwind text color class for the period after the title. Defaults to "text-white".
 * - description (string, required): Paragraph text below the heading.
 * - cardBg (string, optional): Tailwind background class for each card. Defaults to "bg-blue-navy".
 * - items (array, required): Array of card objects with the following shape:
 *     - title (string): Bold card heading.
 *     - description (string, optional): Body text for the card. Used when there are no bullets.
 *     - bullets (array, optional): Array of strings rendered as a checkmark list. Used instead of description.
 *     - bulletIcon (string, optional): URL of the icon used for each bullet. Defaults to the standard check.svg.
 *     - image (string): URL of the icon image displayed at the bottom of the card.
 */

const ServiceCardsSection = ({
  bgColor,
  title,
  titleColor = "text-blue-navy",
  accentColor = "text-white",
  description,
  cardBg = "bg-blue-navy",
  items,
}) => {
  return (
    <section className={`${bgColor} my-16 py-16 px-4 md:px-8 lg:px-16 xl:px-24`}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 text-center md:text-left">
          <h2 className={`text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold ${titleColor} mb-4`}>
            {title}
          </h2>
          <p className="!text-sm md:!text-base xl:!text-2xl !font-medium max-w-lg xl:max-w-2xl mx-auto md:mx-0">
            {description}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((item, index) => (
            <div
              key={index}
              className={`${cardBg} rounded-2xl p-8 lg:p-10 xl:p-14 flex flex-col text-white text-left xl:min-h-[620px]`}
            >
              <h3 className="font-bold text-2xl lg:text-3xl mb-6">
                {item.title}
              </h3>
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/white-horizontal-line.svg"
                alt=""
                className="w-14 xl:w-24 mt-4 mb-12"
              />
              {item.bullets ? (
                <ul className="flex flex-col gap-3 flex-grow mb-8">
                  {item.bullets.map((bullet, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <img
                        src={item.bulletIcon || "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/check.svg"}
                        alt=""
                        className="w-5 h-5 mt-0.5 min-w-[20px]"
                      />
                      <span className="!text-sm lg:!text-base xl:!text-lg leading-relaxed">{bullet}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="!text-sm lg:!text-base xl:!text-xl leading-relaxed flex-grow mb-8">
                  {item.description}
                </p>
              )}
              <img
                src={item.image}
                alt={`${item.title} icon`}
                className="w-32 lg:w-40 xl:w-64 self-center my-8"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceCardsSection;
